'use client'

import { useEffect, useRef, useState } from 'react'

import { GameShell } from '@/components/losi-games/game-shell'

import { mountGameCanvas } from '@/lib/losi-games/canvas'
import { difficultyScale, type Difficulty } from '@/lib/losi-games/difficulty'
import { drawChevronShip, drawEyeCapsule, drawInkDot, drawLobeEnemy } from '@/lib/losi-games/glyphs'
import { bestLocalScore, recordScoreAndWhisper } from '@/lib/losi-games/leaderboard'
import { resolveGameTheme } from '@/lib/losi-games/theme'
import type { LosiGameRankWhisper } from '@/lib/losi-games/types'

const TURN_MAX = (30 * Math.PI) / 180

type Enemy = {
  x: number
  y: number
  vy: number
  kind: 'eye' | 'lobe'
  halfH: number
  halfW: number
  r: number
}
type Bullet = { x: number; y: number; vy: number }

export function SpaceGame({ colorId = 'creme' }: { colorId?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [score, setScore] = useState(0)
  const [best, setBest] = useState(0)
  const [level, setLevel] = useState(1)
  const [menu, setMenu] = useState(true)
  const [over, setOver] = useState(false)
  const [won, setWon] = useState(false)
  const [whisper, setWhisper] = useState<LosiGameRankWhisper | null>(null)
  const [theme, setTheme] = useState(() => resolveGameTheme(colorId))
  const themeRef = useRef(theme)
  const startFn = useRef<(d: Difficulty) => void>()
  const restartFn = useRef<() => void>()

  useEffect(() => {
    const next = resolveGameTheme(colorId)
    themeRef.current = next
    setTheme(next)
  }, [colorId])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    themeRef.current = resolveGameTheme(colorId)
    let cleanup: (() => void) | undefined

  setBest(bestLocalScore('space-shooter'))
  const { ctx, size, dispose } = mountGameCanvas(canvas)

  let { w, h } = size()
  const ship = { x: w / 2, y: h - 64, size: 22, ang: 0 }
  const enemies: Enemy[] = []
  const bullets: Bullet[] = []
  let spawnAcc = 0
  let fireAcc = 0
  let last = performance.now()
  let pointerX = w / 2
  let scoreAcc = 0
  let levelAcc = 1
  let killsLeft = 0
  let diff: Difficulty = 'normal'
  let scale = 1
  let playing = false
  let alive = true
  let raf = 0

  const quotaFor = (lv: number) => Math.round((6 + lv * 3) * (diff === 'easy' ? 0.85 : diff === 'hard' ? 1.15 : 1))

  const beginLevel = (lv: number) => {
    levelAcc = lv
    setLevel(lv)
    killsLeft = quotaFor(lv)
    enemies.length = 0
    bullets.length = 0
    spawnAcc = 0
  }

  const onMove = (e: PointerEvent) => {
    const rect = canvas.getBoundingClientRect()
    pointerX = e.clientX - rect.left
  }
  canvas.addEventListener('pointermove', onMove)

  const finish = (didWin: boolean) => {
    playing = false
    setOver(!didWin)
    setWon(didWin)
    setWhisper(recordScoreAndWhisper('space-shooter', scoreAcc))
    setBest(bestLocalScore('space-shooter'))
  }

  startFn.current = (d: Difficulty) => {
    diff = d
    scale = difficultyScale(d)
    scoreAcc = 0
    setScore(0)
    setMenu(false)
    setOver(false)
    setWon(false)
    setWhisper(null)
    playing = true
    ship.x = w / 2
    beginLevel(1)
  }

  restartFn.current = () => {
    setMenu(true)
    setOver(false)
    setWon(false)
    playing = false
    setBest(bestLocalScore('space-shooter'))
  }

  const loop = (now: number) => {
    if (!alive) return
    const dt = Math.min(0.033, (now - last) / 1000)
    last = now
    ;({ w, h } = size())
    ship.y = h - 64
    const th = themeRef.current

    if (playing) {
      const nx = (pointerX - ship.x) / Math.max(40, w * 0.25)
      ship.ang +=
        (Math.max(-TURN_MAX, Math.min(TURN_MAX, nx * TURN_MAX)) - ship.ang) * Math.min(1, dt * 10)
      ship.x += (pointerX - ship.x) * Math.min(1, dt * 9)
      ship.x = Math.max(28, Math.min(w - 28, ship.x))

      const spawnEvery = Math.max(0.16, (0.95 - levelAcc * 0.055) / scale)
      spawnAcc += dt
      if (spawnAcc > spawnEvery && enemies.length < 10 + levelAcc) {
        spawnAcc = 0
        const kind: Enemy['kind'] = Math.random() > 0.7 ? 'lobe' : 'eye'
        const margin = w * 0.16
        enemies.push({
          x: margin + Math.random() * (w - margin * 2),
          y: -30,
          vy: (55 + levelAcc * 12 + Math.random() * 70) * scale,
          kind,
          halfH: kind === 'eye' ? 16 + Math.random() * 8 : 0,
          halfW: kind === 'eye' ? 7 + Math.random() * 3 : 0,
          r: kind === 'lobe' ? 14 + Math.random() * 10 : 0
        })
      }

      fireAcc += dt
      if (fireAcc > 0.11) {
        fireAcc = 0
        const mx = ship.x + Math.sin(ship.ang) * 8
        const my = ship.y - ship.size * 0.2
        bullets.push({ x: mx, y: my, vy: -520 })
        bullets.push({ x: mx - 7, y: my + 4, vy: -480 })
        bullets.push({ x: mx + 7, y: my + 4, vy: -480 })
      }

      for (const b of bullets) b.y += b.vy * dt
      for (let i = bullets.length - 1; i >= 0; i--) if (bullets[i]!.y < -20) bullets.splice(i, 1)
      for (const e of enemies) e.y += e.vy * dt

      for (let i = enemies.length - 1; i >= 0; i--) {
        const e = enemies[i]!
        let hit = false
        for (let j = bullets.length - 1; j >= 0; j--) {
          const b = bullets[j]!
          const er = e.kind === 'eye' ? Math.max(e.halfW, e.halfH * 0.55) : e.r
          if ((b.x - e.x) ** 2 + (b.y - e.y) ** 2 < (er + 4) ** 2) {
            bullets.splice(j, 1)
            hit = true
            break
          }
        }
        if (hit) {
          enemies.splice(i, 1)
          scoreAcc += e.kind === 'lobe' ? 3 : 1
          setScore(scoreAcc)
          killsLeft -= 1
          if (killsLeft <= 0) {
            scoreAcc += levelAcc >= 8 ? 40 : 25
            setScore(scoreAcc)
            beginLevel(levelAcc + 1)
          }
          continue
        }
        if (e.y > h + 40) {
          enemies.splice(i, 1)
          continue
        }
        const er = e.kind === 'eye' ? Math.max(e.halfW, e.halfH * 0.5) : e.r * 0.85
        if ((e.x - ship.x) ** 2 + (e.y - ship.y) ** 2 < (er + ship.size * 0.45) ** 2) {
          finish(false)
        }
      }
    }

    ctx.fillStyle = th.paper
    ctx.fillRect(0, 0, w, h)
    for (const e of enemies) {
      if (e.kind === 'eye') drawEyeCapsule(ctx, e.x, e.y, e.halfH, e.halfW, 0, th.body)
      else drawLobeEnemy(ctx, e.x, e.y, e.r, 3, th.body)
    }
    for (const b of bullets) drawInkDot(ctx, b.x, b.y, 2.4, th.mark)
    drawChevronShip(ctx, ship.x, ship.y, ship.size, ship.ang, th.body)

    if (playing) {
      ctx.fillStyle = th.mute
      ctx.font = '11px ui-sans-serif, system-ui, sans-serif'
      ctx.textAlign = 'left'
      ctx.fillText(`Level ${levelAcc} · left ${Math.max(0, killsLeft)}`, 12, 22)
    }

    raf = requestAnimationFrame(loop)
  }
  raf = requestAnimationFrame(loop)
  cleanup = () => {
    alive = false
    cancelAnimationFrame(raf)
    dispose()
    canvas.removeEventListener('pointermove', onMove)
  }

    return () => {
      cleanup?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <GameShell
      theme={theme}
      title="Space"
      score={score}
      best={best}
      meta={`lvl ${level}`}
      menu={menu}
      over={over}
      won={won}
      whisper={whisper}
      hint="Clear each quota — levels keep coming, faster and denser."
      onStart={(d) => startFn.current?.(d)}
      onRestart={() => restartFn.current?.()}
    >
      <canvas ref={canvasRef} className="block touch-none" />
    </GameShell>
  )
}
